import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Settings, ChevronLeft, ChevronRight, X, DollarSign, Wrench, AlertCircle, AlertTriangle, ClipboardList, Search, BarChart3, Clock, FileCheck, Gift, Banknote, UserCog, TrendingUp, Package, Wallet, SlidersHorizontal, CalendarDays } from 'lucide-react';

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen, userRole }) => {
  const location = useLocation();

  // Define menus based on role
  const adminMenu = [
    // Overview
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/dashboard/today', label: "Today's Collections", icon: Clock },
    { path: '/dashboard/month', label: 'This Month', icon: BarChart3 },
    { path: '/office/monthly-closing', label: 'Monthly Closing', icon: FileCheck },

    // Customer management
    { type: 'header', label: 'Customers' },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/billing', label: 'Billing & Invoices', icon: FileText },
    { path: '/all-collections', label: 'All Collections', icon: Banknote },

    // Day-to-day field operations
    { type: 'header', label: 'Daily Operations' },
    { path: '/activations', label: 'Activations', icon: ClipboardList },
    { path: '/advance-activations', label: 'Advance Activations', icon: AlertTriangle },
    { path: '/activation-payments', label: 'Pending Payments', icon: Clock },
    { path: '/missing-entries', label: 'Missing Entries', icon: AlertTriangle },
{ path: '/recovery-pending', label: 'Daily Recovery', icon: TrendingUp },
    { path: '/expenses', label: 'Expenses', icon: DollarSign },

    // Complaints & field support
    { type: 'header', label: 'Support' },
    { path: '/complaints', label: 'Complaints', icon: AlertCircle },
    { path: '/technicians', label: 'Technicians', icon: UserCog },

    // Payroll & HR
    { type: 'header', label: 'HR & Payroll' },
    { path: '/employees', label: 'Employees', icon: Users },
    { path: '/payroll', label: 'Payroll', icon: Banknote },
    { path: '/employee-leaves', label: 'Employee Leaves', icon: CalendarDays },
    { path: '/bonus-pool', label: 'Bonus Pool', icon: Gift },

    // Inventory
    { type: 'header', label: 'Inventory' },
    { path: '/stock', label: 'Stock', icon: Package },
    { path: '/installation-requests', label: 'Install Requests', icon: ClipboardList },

    // Admin tools
    { type: 'header', label: 'System' },
    { path: '/tools', label: 'Tools', icon: Wrench },
    { path: '/settings', label: 'Settings', icon: Settings },
    { path: '/admin/feature-permissions', label: 'Feature Access', icon: SlidersHorizontal },
  ];

  const stockMenuItem = { path: '/stock', label: 'Stock Requests', icon: Package };
  const installationRequestMenuItem = { path: '/installation-requests', label: 'Install Invoice Request', icon: ClipboardList };

  const officerMenu = [
    { path: '/officer-dashboard', label: 'My Dashboard', icon: LayoutDashboard },
    { path: '/collections/new', label: 'Quick Collect', icon: DollarSign },
    { path: '/my-collections', label: 'My History', icon: FileText },
    { path: '/officer-pendings', label: 'My Pendings', icon: Wallet },
    { path: '/missing-entries', label: 'Missing Entries', icon: AlertTriangle },
{ type: 'header', label: 'Inventory' },
    stockMenuItem,
    installationRequestMenuItem,
  ];

  const technicianMenu = [
    { path: '/technician-dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/technician/complaints', label: 'Complaints', icon: ClipboardList },
    { path: '/technician/customers', label: 'Search Customer', icon: Search },
    { type: 'header', label: 'Inventory' },
    stockMenuItem,
    installationRequestMenuItem,
  ];

  // For dual-role: officer menu + complaint items (no duplicate dashboard)
  const officerTechnicianMenu = [
    ...officerMenu.filter(i => !['/stock', '/installation-requests'].includes(i.path) && i.label !== 'Inventory'),
    { path: '/technician/complaints', label: 'Complaints', icon: ClipboardList },
    { path: '/technician/customers', label: 'Search Customer', icon: Search },
    { type: 'header', label: 'Inventory' },
    stockMenuItem,
    installationRequestMenuItem,
  ];

  const clerkMenu = [
    { path: '/activations', label: 'Activations', icon: ClipboardList },
    { path: '/activation-payments', label: 'Pending Payments', icon: Clock },
    { path: '/employee-leaves', label: 'Employee Leaves', icon: CalendarDays },

    { type: 'header', label: 'Support' },
    { path: '/complaints', label: 'Complaints', icon: AlertCircle },
  ];

  const managerMenu = [
    { type: 'header', label: 'Customers' },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/billing', label: 'Billing & Invoices', icon: FileText },
    { path: '/installation-requests', label: 'Install Requests', icon: ClipboardList },

    { type: 'header', label: 'Daily Operations' },
    { path: '/activations', label: 'Activations', icon: ClipboardList },
    { path: '/advance-activations', label: 'Advance Activations', icon: AlertTriangle },
    { path: '/activation-payments', label: 'Pending Payments', icon: Clock },

    { type: 'header', label: 'Support' },
    { path: '/complaints', label: 'Complaints', icon: AlertCircle },
    { path: '/technicians', label: 'Technicians', icon: UserCog },

    { type: 'header', label: 'HR & Payroll' },
    { path: '/employees', label: 'Employees', icon: Users },
    { path: '/payroll', label: 'Payroll', icon: Banknote },
    { path: '/employee-leaves', label: 'Employee Leaves', icon: CalendarDays },
    { path: '/bonus-pool', label: 'Bonus Pool', icon: Gift },

    { type: 'header', label: 'Inventory' },
    { path: '/stock', label: 'Stock', icon: Package },

    { type: 'header', label: 'Finance' },
    { path: '/expenses', label: 'Daily Expenses', icon: DollarSign },

    { type: 'header', label: 'Tools' },
    { path: '/tools', label: 'Import Tools', icon: Wrench },
  ];

  const navItems = userRole === 'admin'
    ? adminMenu
    : userRole === 'manager'
      ? managerMenu
      : userRole === 'officer_technician'
        ? officerTechnicianMenu
        : userRole === 'technician'
          ? technicianMenu
          : userRole === 'clerk'
            ? clerkMenu
            : officerMenu;

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)}
        ></div>
      )}

      <aside
        className={`bg-slate-900 text-white h-screen fixed left-0 top-0 flex flex-col transition-all duration-300 ease-in-out z-50 ${
          isCollapsed ? 'w-20' : 'w-64'
        } ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          {!isCollapsed && (
            <span className="text-xl font-bold whitespace-nowrap overflow-hidden">
              OpticFlow ISP
            </span>
          )}
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors ml-auto hidden lg:block"
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
          
          <button 
            onClick={() => setIsMobileOpen(false)}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 lg:hidden"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 py-6 space-y-1 px-2 overflow-y-auto">
          {navItems.map((item) => {
            // Section header
            if (item.type === 'header') {
              return (
                <div
                  key={item.label}
                  className={`px-3 pt-5 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 transition-all duration-300 ${
                    isCollapsed ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'
                  }`}
                >
                  {item.label}
                </div>
              );
            }

            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 group relative ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />

                <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${
                  isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                }`}>
                  {item.label}
                </span>

                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                    {item.label}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 text-center">
          <p className={`text-xs text-slate-500 transition-all duration-300 ${isCollapsed ? 'opacity-0 hidden' : 'opacity-100'}`}>
            © 2026 OpticFlow
          </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
